/**
 * Chap menyu rolga qarab (redesign v2, 2026-10-17):
 * - Recruiter (recruiter): Today · Drivers · Pipeline · Jobs · Tasks -- faqat ish, analitika yo'q.
 * - Manager (filial menejeri): Today · Drivers · Pipeline · Jobs · Team · Approvals · Reports · Budget --
 *   oxirgi uchtasi ruxsat bo'yicha (owner belgilaydi).
 * - Owner: Overview · People · Finance · Drivers · Carriers · Reports · Approvals · Settings -- analitika va boshqaruv;
 *   driver qo'shish bosh sahifada emas (⌘K va Drivers sahifasida).
 * Carriers va Jobs bir-biridan sahifa ichidagi tanlov bilan ochiladi, Pipeline -- Drivers ichidagi "Doska".
 * Ruxsat backend'da; bu faqat ko'rinish.
 */
import {
  BadgeCheck,
  Briefcase,
  Car,
  ChartColumn,
  House,
  LayoutDashboard,
  ListChecks,
  Settings,
  SquareKanban,
  Truck,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { can, type Me } from "@/features/auth/types";

export interface Counts {
  approvals: number;
  notifications: number;
  tasks_overdue: number;
}

export interface NavItem {
  to: string;
  /** tarjima kaliti: common:nav.<label> */
  label: "today" | "overview" | "people" | "team" | "finance" | "budget" | "drivers" | "pipeline" | "jobs" | "carriers" | "reports" | "approvals" | "settings" | "tasks";
  icon: LucideIcon;
  badge?: (c: Counts) => number;
  badgeTone?: "red" | "grey";
  /** Faollik: aniq yo'l (end) yoki prefiks */
  match?: (pathname: string) => boolean;
}

type Scope = Pick<Me, "role" | "permissions">;

const TODAY: NavItem = { to: "/", label: "today", icon: House, match: (p) => p === "/" };
const DRIVERS: NavItem = { to: "/drivers", label: "drivers", icon: Car };
const PIPELINE: NavItem = { to: "/pipeline", label: "pipeline", icon: SquareKanban };
const JOBS: NavItem = { to: "/jobs", label: "jobs", icon: Briefcase };
const CARRIERS: NavItem = { to: "/carriers", label: "carriers", icon: Truck };
const TASKS: NavItem = { to: "/tasks", label: "tasks", icon: ListChecks, badge: (c) => c.tasks_overdue, badgeTone: "red" };
const TEAM: NavItem = { to: "/people", label: "team", icon: Users };
const PEOPLE: NavItem = { to: "/people", label: "people", icon: Users };
const APPROVALS: NavItem = { to: "/approvals", label: "approvals", icon: BadgeCheck, badge: (c) => c.approvals, badgeTone: "red" };
const REPORTS: NavItem = { to: "/reports", label: "reports", icon: ChartColumn };
const BUDGET: NavItem = { to: "/budget", label: "budget", icon: Wallet, match: (p) => p.startsWith("/budget") };
const FINANCE: NavItem = { ...BUDGET, label: "finance" };
const SETTINGS: NavItem = { to: "/admin/users", label: "settings", icon: Settings, match: (p) => p.startsWith("/admin") };
const OVERVIEW: NavItem = { ...TODAY, label: "overview", icon: LayoutDashboard };

export function menuFor(me: Scope): NavItem[] {
  switch (me.role) {
    case "owner":
      return [OVERVIEW, PEOPLE, FINANCE, DRIVERS, CARRIERS, REPORTS, APPROVALS, SETTINGS];
    case "branch_manager":
      return [
        TODAY,
        DRIVERS,
        PIPELINE,
        JOBS,
        TEAM,
        ...(can(me, "approvals") ? [APPROVALS] : []),
        ...(can(me, "reports") ? [REPORTS] : []),
        ...(can(me, "budget") || can(me, "pay") ? [BUDGET] : []),
      ];
    default:
      return [TODAY, DRIVERS, PIPELINE, JOBS, TASKS];
  }
}

export function isNavActive(item: NavItem, pathname: string): boolean {
  if (item.match) return item.match(pathname);
  return pathname === item.to || pathname.startsWith(item.to + "/");
}

/** ⌘K dagi "O'tish" ro'yxati: menyu + menyuda bo'lmagan, lekin rolga ochiq sahifalar */
export function goToItems(me: Scope): NavItem[] {
  const menu = menuFor(me);
  const extra: NavItem[] = [];
  const add = (item: NavItem) => {
    if (!menu.some((m) => m.to === item.to && m.label === item.label)) extra.push(item);
  };
  add(PIPELINE);
  add(JOBS);
  add(CARRIERS);
  add(TASKS);
  if (me.role !== "recruiter") add(TEAM);
  if (can(me, "reports")) add(REPORTS);
  if (can(me, "approvals")) add(APPROVALS);
  if (can(me, "budget") || can(me, "pay")) add(BUDGET);
  if (can(me, "users")) add(SETTINGS);
  return [...menu, ...extra];
}
